import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { ClerkProvider } from "@clerk/react";
import { PostHogProvider } from "@posthog/react";
import {
  initSentry,
  tracePropagationTargets,
} from "@sill/shared/observability/sentry";
import { AppErrorBoundary } from "@sill/shared/observability/AppErrorBoundary";
import { ToastProvider } from "./components/primitives/toast";
import { ErrorPanel } from "./components/common/AsyncStates";
import { PostHogIdentity } from "./components/PostHogIdentity";
import { App } from "./App";
import { getBrowserPathname, isDevPreviewPath } from "./lib/dev-preview";

// Sentry must boot before createRoot so BrowserTracing sees the first
// navigation. Empty DSN → no-op (local dev needs no Sentry credentials).
initSentry({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_SENTRY_ENV ?? "local",
  release: import.meta.env.VITE_GIT_SHA ?? "dev",
  tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
  apiHosts: tracePropagationTargets(import.meta.env.VITE_API_URL),
});

// Brand typeface: Instrument Sans (text/UI/figures) + Fraunces (display, full axes:
// opsz + WONK/SOFT, so WONK can be turned off) + JetBrains Mono (financials).
import "@fontsource-variable/instrument-sans/index.css";
import "@fontsource-variable/fraunces/full.css";
import "@fontsource/jetbrains-mono/400.css";
import "@fontsource/jetbrains-mono/500.css";
import "@fontsource/jetbrains-mono/700.css";
import "./index.css";

const CLERK_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
const CLERK_SIGN_IN_URL = import.meta.env.VITE_CLERK_SIGN_IN_URL || "/login";
const CLERK_SIGN_UP_URL = import.meta.env.VITE_CLERK_SIGN_UP_URL || "/join";
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST =
  import.meta.env.VITE_POSTHOG_HOST || "https://us.i.posthog.com";

// Preview-bypass: render without Clerk when no key is set so the shell can be
// driven in a browser before auth is wired (mirrors consumer's preview path).
// While the surface is pre-launch we extend the bypass to all DEV routes — the
// whole app is built preview-first, so a missing key in `vite dev` must not
// blank the screen. Explicit dev-preview routes (/preview/*) bypass in any
// mode. In a production build a missing key stays a hard error.
const pathname = getBrowserPathname();
const shouldBypassClerk =
  !CLERK_KEY && (import.meta.env.DEV || isDevPreviewPath(pathname));

if (!CLERK_KEY && !shouldBypassClerk) {
  throw new Error("Missing VITE_CLERK_PUBLISHABLE_KEY");
}

const appShell = POSTHOG_KEY ? (
  <PostHogProvider
    apiKey={POSTHOG_KEY}
    options={{
      api_host: POSTHOG_HOST,
      autocapture: true,
      capture_pageview: "history_change",
      capture_pageleave: true,
    }}
  >
    <PostHogIdentity />
    <ToastProvider>
      <App />
    </ToastProvider>
  </PostHogProvider>
) : (
  <ToastProvider>
    <App />
  </ToastProvider>
);

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <AppErrorBoundary
      fallback={({ reset }) => (
        <ErrorPanel
          title="Something went wrong"
          message="An unexpected error occurred. The team has been notified. Try again, or reload the page if the problem persists."
          onRetry={reset}
        />
      )}
    >
      {shouldBypassClerk ? (
        appShell
      ) : (
        <ClerkProvider
          publishableKey={CLERK_KEY}
          signInUrl={CLERK_SIGN_IN_URL}
          signUpUrl={CLERK_SIGN_UP_URL}
          // Inject Clerk's runtime styles into the predeclared `clerk` cascade
          // layer (see index.css `@layer theme, base, clerk, components, utilities`).
          // Without this, clerk-js injects UNlayered CSS — which outranks every
          // layered rule and settles after first paint, causing a style flash.
          // Mirrors the consumer surface (consumer/src/main.tsx).
          appearance={{ cssLayerName: "clerk" }}
        >
          {appShell}
        </ClerkProvider>
      )}
    </AppErrorBoundary>
  </StrictMode>,
);
